<template>
  <div v-if="isView" class="agent-todo">
    <sub-title title="今日待办"></sub-title>
    <AgentDesc :avatar-index="11" name="日程助手"></AgentDesc>
    <div class="agent-todo-body">
      <template v-if="nextTime && nextName">
        <div class="todo-desc" v-html="desc"></div>
        <div class="todo-card" @click="openSchedule">
          <div class="todo-card__left">
            <div class="time">{{ showTimeFormat(nextTime) }}</div>
            <div class="title">{{ nextName }}</div>
          </div>
          <div class="todo-card__right">
            <i class="icon-arrow-right-line t-iconfont"></i>
          </div>
        </div>
        <div class="todo-more" @click="openSchedule">
          <span>查看其他计划</span>
        </div>
      </template>
      <template v-else-if="aiCount > 0">
        <div class="todo-desc">暂无待完成的访客计划，看看我的推荐吧 ~</div>
        <template v-if="isMobile()">
          <div class="todo-card" @click="openRecommend">
            <div class="todo-card__left updown">
              <div class="title">{{ aiName }}</div>
              <div class="tag">
                <span class="tag-icon">
                  <i class="icon-sparkling-2-fill t-iconfont"></i>
                </span>
                <span class="tag-text">AI 推荐任务（{{ aiCount }}）</span>
              </div>
            </div>
            <div class="todo-card__right">
              <i class="icon-arrow-right-line t-iconfont"></i>
            </div>
          </div>
          <div class="todo-more" @click="openRecommend">
            <span>查看其他推荐</span>
          </div>
        </template>
      </template>
      <template v-else>
        <div class="todo-desc">
          暂无待完成的访客计划，去 <span class="new" @click="handleCreatePlan">新建</span>
        </div>
      </template>
      <!-- pc端展示逻辑 -->
      <template v-if="aiCount > 0 && !isMobile()">
        <PcRecommendTask @openPcDetail="handleOpenDetail" @pcAddPlan="handleAddPlan" />
      </template>
    </div>
    <AiUseXbbForm
      v-if="xbbUrl && showXbb"
      v-model:loading="iframeLoading"
      v-model:show-xbb="showXbb"
      :xbb-url="xbbUrl"
      @handleXbbSuccess="handleXbbSuccess"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue'
import subTitle from './components/sub-title.vue'
import AgentDesc from './components/agent-desc.vue'
import PcRecommendTask from './components/pc-recommend-task.vue'
import { getScheduleList } from '@/api/app-home.js'
import xbb from '@xbb/xbb-utils'
import { isMobile } from '@/utils'
import xbbSdk from 'xbb-sdk'
import { getCustomRecordInfo, blindAddPlan } from '@/api'
import AiUseXbbForm from '@/components/ai-use-xbbform/index.vue'
import AppMessage from '@/components/message/index.js'

const listLoading = ref(false)
const num = ref(0)
const time = ref('')
const aiCount = ref(0)
const aiName = ref('')
const nextName = ref('')
const nextTime = ref('')
const dataList = ref([])
const isView = ref(0)

const detailId = ref('')
const sendXbbParams = ref({})
const iframeLoading = ref(false)
const showXbb = ref(false)
const xbbUrl = ref('')

const desc = computed(
  () =>
    `还有 <span class="highlight">${num.value}</span> 个访客计划未完成，下个计划 <span class="highlight">${time.value}</span> 后开始 ~`
)
const emits = defineEmits(['openSchedule', 'openRecommend', 'refresh'])

const openSchedule = () => {
  emits('openSchedule', isView.value)
}

const showTimeFormat = (time) => {
  if (!time) return '未知'
  return xbb.formatTime(time, 'HH:mm')
}

// 工具函数
const timeFormat = (seconds) => {
  if (!seconds) return '未知'
  if (seconds < 60) return `${seconds}秒`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}分钟`
  return `${Math.floor(seconds / 3600)}小时`
}

// 列表加载
const getList = async () => {
  try {
    listLoading.value = true
    const res = await getScheduleList({
      page: 1,
      pageSize: 10,
      date: xbb.formatTime(Date.parse(new Date()), 'yyyy-MM-dd')
    })
    num.value = res.result.pendingPlanCount
    time.value = timeFormat(res.result.delaySeconds)
    isView.value = res.result.isView
    nextName.value = res.result.nextPendingPlanCorpName
    nextTime.value = res.result.nextPendingPlanTime
    aiCount.value = res.result.aiRecommendedTaskCount
    aiName.value = res.result.homePageCorpName
    dataList.value = res.result.planList
  } catch (error) {
    console.log(error)
  }
  listLoading.value = false
}

const refresh = () => {
  dataList.value = []
  num.value = 0
  time.value = ''
  aiCount.value = 0
  getList()
}

const openRecommend = () => {
  emits('openRecommend', isView.value)
}

const getParams = async (type) => {
  const res = await getCustomRecordInfo({ businessType: type })
  const { appId, menuId, formId, businessType, subBusinessType, frontUrl } = res.result
  return { appId, menuId, formId, businessType, subBusinessType, frontUrl }
}

const handleCreatePlan = async () => {
  if (isMobile()) {
    iframeLoading.value = true
    const path = 'application/newForm'
    const res = await getParams(601)
    const { appId, menuId, formId, businessType, subBusinessType, frontUrl } = res
    const targetUrl = xbb.isDevEnv() ? 'http://localhost:8080/' : frontUrl
    xbbUrl.value = `${targetUrl}?isAi=1&useAiPosition=1&detailId=empty#/${path}?appId=${appId}&menuId=${menuId}&formId=${formId}&saasMark=1&distributorMark=0&businessType=${businessType}&subBusinessType=${subBusinessType}&timestamp=${new Date().getTime()}&isAiRoot=1`
    showXbb.value = true
  } else {
    const params = await getParams(601)
    sendXbbParams.value = {
      appId: params.appId,
      menuId: params.menuId,
      formId: params.formId,
      saasMark: 1,
      businessType: params.businessType,
      subBusinessType: params.subBusinessType
    }
    const data = {
      appId: params.appId,
      menuId: params.menuId,
      formId: params.formId,
      saasMark: 1,
      businessType: params.businessType,
      subBusinessType: params.subBusinessType
    }
    if (detailId.value) {
      data.detailId = detailId.value
    }
    xbbSdk.sendMessage('addNewForm', data)
  }
}

const handleAddPlan = (id) => {
  detailId.value = id
  handleCreatePlan()
}

const handleOpenDetail = (item) => {
  emits('openPcDetail', item)
}

/**
 * 处理销帮帮成功回调 （web端）
 * @param e
 */
const xbbIframeWeb = (e) => {
  if (e.data?.type === 'aiSuccessWeb') {
    const params = e.data.data
    const result = e.data.result
    // 校验是否是当前表单 因为销帮帮可以关联新建
    if (
      params.appId === sendXbbParams.value.appId &&
      params.menuId === sendXbbParams.value.menuId &&
      params.formId === sendXbbParams.value.formId &&
      params.businessType === sendXbbParams.value.businessType
    ) {
      if (detailId.value) {
        AppMessage.success('新建成功')
        blindAddPlan({
          detailId: detailId.value,
          communicatePlanId: result.dataId
        }).then(() => {
          detailId.value = ''
          refresh()
        })
      } else {
        refresh()
      }
    }
  }
}

const handleXbbSuccess = () => {
  showXbb.value = false
  AppMessage.success('新建成功')
  emits('refresh')
}

onMounted(() => {
  getList()
  window.addEventListener('message', xbbIframeWeb)
})
onBeforeUnmount(() => {
  window.removeEventListener('message', xbbIframeWeb)
})

defineExpose({
  refresh
})
</script>

<style lang="scss" scoped>
.agent-todo {
  display: flex;
  flex-direction: column;
  position: relative;
  margin-bottom: 24px;
  &-body {
    flex: 1;
    padding-left: 32px;
    display: flex;
    flex-direction: column;
    position: relative;
    .todo-desc {
      line-height: 20px;
      font-size: 13px;
      color: #646566;
      margin-bottom: 8px;
      :deep(.highlight) {
        font-size: 14px;
        font-weight: 600;
      }
      .new {
        color: #248df5;
        cursor: pointer;
      }
    }
    .todo-card {
      border-radius: 16px;
      padding: 12px;
      background: #ffffff;
      box-shadow: 0px 2px 30px 2px rgba(0, 0, 0, 0.05);
      display: flex;
      align-items: center;
      margin-bottom: 8px;
      .todo-card__left {
        flex: 1;
        overflow: hidden;
        display: flex;
        align-items: center;
        margin-right: 12px;
        font-size: 14px;
        font-weight: 600;
        line-height: 20px;
        color: #323233;
        .time {
          flex: 0 0 auto;
          margin-right: 12px;
          padding-right: 12px;
          border-right: 1px dashed #f2f3f5;
        }
        .title {
          flex: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
      .updown {
        align-items: baseline;
        flex-direction: column;

        .tag {
          flex: 0 0 auto;
          display: inline-flex;
          align-items: center;
          margin-top: 4px;
          padding: 2px 8px;
          border-radius: 16px;
          background: linear-gradient(279deg, #eef2ff 1%, #f5f3ff 100%);
          .tag-icon {
            width: 16px;
            height: 16px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            i {
              font-size: 12px;
              line-height: 1;
              background: linear-gradient(314deg, #1574ff 2%, #5952ff 51%, #a969fe 96%);
              -webkit-background-clip: text;
              -webkit-text-fill-color: transparent;
            }
          }
          .tag-text {
            font-size: 12px;
            font-weight: 400;
            line-height: 16px;
            background: linear-gradient(314deg, #1574ff 2%, #5952ff 51%, #a969fe 96%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
          }
        }
      }
      .todo-card__right {
        flex: 0 0 auto;
        i {
          font-size: 18px;
          color: #969799;
          cursor: pointer;
        }
      }
    }
    .todo-more {
      font-size: 13px;
      line-height: 18px;
      color: #969799;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
    }
  }
}
</style>
